import { TaskStatus, PositionSide, TradeStatus } from '@/services/enums';
/**
 * 回测系统前端服务
 */

import { apiClient } from './apiClient';
import { BarInterval, OrderSide, InstType, StrategyCode } from '@/services/enums';

export interface Strategy {
  code: StrategyCode;
  name: string;
  category: string;
  description: string;
  defaultParams: Record<string, any>;
  paramDescriptions: Record<string, string>;
  priority?: number;
}

export interface BacktestConfig {
  name?: string;
  strategyCode: StrategyCode;
  strategyParams?: Record<string, any>;
  symbol: string;
  instType?: InstType;
  startTime: string;
  endTime: string;
  timeframe: BarInterval;
  initialCapital: number;
  leverage?: number;
  positionSize?: number;
  makerFee?: number;
  takerFee?: number;
  slippage?: number;
}

export interface BacktestResult {
  id: number;
  strategyId: number;
  name: string;
  symbol: string;
  instType: string;
  startTime: string;
  endTime: string;
  timeframe: string;
  initialCapital: number;
  leverage: number;
  positionSize: number;
  status: TaskStatus;

  // 结果指标
  totalTrades?: number;
  winTrades?: number;
  lossTrades?: number;
  winRate?: number;
  totalReturn?: number;
  totalReturnPct?: number;
  annualReturn?: number;
  maxDrawdown?: number;
  sharpeRatio?: number;
  sortinoRatio?: number;
  calmarRatio?: number;
  profitFactor?: number;
  avgHoldingTime?: number;
  finalCapital?: number;

  createdAt: string;
  completedAt?: string;
  errorMessage?: string;

  strategy?: {
    name: string;
    code: string;
  };
}

export interface BacktestTrade {
  id: number;
  tradeNo: number;
  side: OrderSide;
  positionSide: PositionSide;
  entryTime: string;
  entryPrice: number;
  entryAmount: number;
  entryValue: number;
  entrySignal?: string;
  exitTime?: string;
  exitPrice?: number;
  exitSignal?: string;
  exitReason?: string;
  pnl?: number;
  pnlPct?: number;
  fee?: number;
  netPnl?: number;
  holdingTime?: number;
  status: TradeStatus;
}

export interface EquityPoint {
  timestamp: string;
  equity: number;
  balance: number;
  position: number;
  unrealizedPnl: number;
  drawdown: number;
  highWaterMark: number;
}

export interface DataRange {
  start: string;
  end: string;
}

export interface DataIntegrity {
  complete: boolean;
  missingCount: number;
  coverage: number;
}

export interface BacktestTaskStatus {
  backtestId: number;
  status: TaskStatus;
  progress?: number;
  message?: string;
  result?: any;
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

export class BacktestService {
  /**
   * 获取所有可用策略
   */
  async getStrategies(): Promise<Strategy[]> {
    const response = await apiClient.get<Strategy[]>('/backtest/strategies');

    if (!response.success) {
      throw new Error(response.message || '获取策略列表失败');
    }

    return response.data || [];
  }

  /**
   * 获取策略默认参数
   */
  async getStrategyParams(code: StrategyCode): Promise<Record<string, any>> {
    const response = await apiClient.get<Record<string, any>>(`/backtest/strategies/${code}/params`);

    if (!response.success) {
      throw new Error(response.message || '获取策略参数失败');
    }

    return response.data;
  }

  /**
   * 获取可用交易对
   */
  async getSymbols(): Promise<string[]> {
    const response = await apiClient.get<string[]>('/backtest/symbols');

    if (!response.success) {
      throw new Error(response.message || '获取交易对列表失败');
    }

    return response.data || [];
  }

  /**
   * 获取数据时间范围
   */
  async getDataRange(symbol: string, timeframe: BarInterval): Promise<DataRange | null> {
    const response = await apiClient.get<DataRange | null>('/backtest/data-range', {
      params: { symbol, timeframe },
    });

    if (!response.success) {
      throw new Error(response.message || '获取数据时间范围失败');
    }

    return response.data;
  }

  /**
   * 检查数据完整性
   */
  async checkDataIntegrity(
    symbol: string,
    timeframe: BarInterval,
    startTime: string,
    endTime: string,
  ): Promise<DataIntegrity> {
    const response = await apiClient.get<DataIntegrity>('/backtest/data-integrity', {
      params: { symbol, timeframe, startTime, endTime },
    });

    if (!response.success) {
      throw new Error(response.message || '检查数据完整性失败');
    }

    return response.data;
  }

  /**
   * 执行回测（异步）
   */
  async runBacktest(config: BacktestConfig): Promise<{ backtestId: number; status: string }> {
    const response = await apiClient.post<{ backtestId: number; status: string }>('/backtest/run', config);

    if (!response.success) {
      throw new Error(response.message || '启动回测失败');
    }

    return response.data;
  }

  /**
   * 执行回测（同步等待结果）
   */
  async runBacktestSync(config: BacktestConfig): Promise<any> {
    const response = await apiClient.post<any>('/backtest/run-sync', config);

    if (!response.success) {
      throw new Error(response.message || '执行回测失败');
    }

    return response.data;
  }

  /**
   * 获取回测任务状态
   */
  async getBacktestStatus(id: number): Promise<BacktestTaskStatus> {
    const response = await apiClient.get<BacktestTaskStatus>(`/backtest/${id}/status`);

    if (!response.success) {
      throw new Error(response.message || '获取回测状态失败');
    }

    return response.data;
  }

  /**
   * 获取回测列表
   */
  async getBacktestList(params: {
    page?: number;
    pageSize?: number;
    status?: string;
    strategyCode?: string;
    symbol?: string;
  }): Promise<{ data: BacktestResult[]; pagination: any }> {
    const response = await apiClient.get<{ data: BacktestResult[]; pagination: any }>('/backtest/list', { params });

    if (!response.success) {
      throw new Error(response.message || '获取回测列表失败');
    }

    return response.data;
  }

  /**
   * 获取回测详情
   */
  async getBacktestDetail(id: number): Promise<BacktestResult> {
    const response = await apiClient.get<BacktestResult>(`/backtest/${id}`);

    if (!response.success) {
      throw new Error(response.message || '获取回测详情失败');
    }

    return response.data;
  }

  /**
   * 获取回测交易记录
   */
  async getBacktestTrades(
    id: number,
    params?: { page?: number; pageSize?: number },
  ): Promise<{ data: BacktestTrade[]; pagination: any }> {
    const response = await apiClient.get<{ data: BacktestTrade[]; pagination: any }>(`/backtest/${id}/trades`, {
      params,
    });

    if (!response.success) {
      throw new Error(response.message || '获取回测交易记录失败');
    }

    return response.data;
  }

  /**
   * 获取回测权益曲线
   */
  async getBacktestEquity(id: number): Promise<EquityPoint[]> {
    const response = await apiClient.get<EquityPoint[]>(`/backtest/${id}/equity`);

    if (!response.success) {
      throw new Error(response.message || '获取回测权益曲线失败');
    }

    return response.data;
  }

  /**
   * 删除回测
   */
  async deleteBacktest(id: number): Promise<any> {
    const response = await apiClient.delete<any>(`/backtest/${id}`);

    if (!response.success) {
      throw new Error(response.message || '删除回测失败');
    }

    return response.data;
  }
}

export const backtestService = new BacktestService();
